import { BlurFade } from "@/components/magic/blur-fade";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Image } from "@/components/ui/image";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArtworkCard } from "@/features/artwork/components/artwork-card";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMeasure } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "motion/react";
import { useMemo } from "react";

export const Route = createFileRoute("/")({
  component: RouteComponent,
});

function FeaturedSlides() {
  const slides = [...Array(12)].map((_, i) => ({
    imageUrl: `https://picsum.photos/id/${i + 131}/800/450`,
    title: `Slide ${i + 1}`,
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
    href: `https://picsum.photos/id/${i + 131}/800/450`,
  }));

  return (
    <Carousel>
      <CarouselContent className="snap-x">
        {slides.map((slide, index) => (
          <CarouselItem key={slide.title} className="basis-[23%] snap-center">
            <motion.article
              className="relative flex flex-col justify-end w-full px-4 pb-5 overflow-hidden group bg-gray-subtle-bg aspect-golden isolate"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: "easeOut",
              }}
            >
              <Image
                alt=""
                src={slide.imageUrl}
                className="absolute inset-0 object-cover -z-10 size-full"
                layout="fullWidth"
              />
              <div className="absolute inset-0 -z-10 bg-linear-to-t from-black via-black/40" />
              <div className="absolute inset-0 -z-10 rounded-2xl ring-1 ring-black/10 ring-inset" />

              <h3 className="text-xl font-bold text-white">
                <Link to={slide.href}>
                  <span className="absolute inset-0" />
                  {slide.title}
                </Link>
              </h3>
              <p className="text-sm text-white/70 line-clamp-2">
                {slide.description}
              </p>
            </motion.article>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
  );
}

function ArtworkGallery({
  type: _type,
}: { type: "default" | "trending" | "latest" | "subscribed" }) {
  const items = [...Array(28)].map((_, i) => i);

  const [ref, { width }] = useMeasure();
  const preferredColumns = useMemo(
    () => Math.max(2, Math.floor((width ?? 0) / 200)),
    [width],
  );

  return (
    <motion.div
      ref={ref}
      className="grid gap-1"
      style={{
        gridTemplateColumns: `repeat(${preferredColumns}, minmax(0, 1fr))`,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {items.map((i) => (
        <BlurFade key={i.toString()} delay={((i % 6) % 6) * 0.1} inView>
          <ArtworkCard
            imageUrl={`https://picsum.photos/seed/artwork-${i + 31}/512.webp`}
            authorAvatarUrl=""
            authorName="SX"
            title={`Title ${i + 1}`}
            description="Lorem ipsum dolor sit amet, consectetur adipiscing elit."
            link={{
              to: "/artwork/$artworkId/modal",
              params: { artworkId: i.toString() },
            }}
          />
        </BlurFade>
      ))}
    </motion.div>
  );
}

function PromotedArtists() {
  return (
    <Carousel>
      <CarouselContent className="snap-x -ml-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <CarouselItem key={i} className="basis-[30%] snap-center pl-4">
            <div className="bg-graya-subtle-bg text-gray-fg-primary p-4">
              <div className="flex items-start mb-4">
                {/* Artist Info Section */}
                <div className="flex items-center gap-2 flex-1">
                  <img
                    src="https://placehold.co/64x64"
                    alt="Artist avatar"
                    width={48}
                    height={48}
                    className="size-12"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-medium">ento_mor</h2>
                      <Badge variant="default" size="xs">
                        Lv. 3
                      </Badge>
                    </div>
                    <p className="text-gray-400 text-xs max-w-3/4 truncate">
                      Pixel artist/ 22 y.o./ looking for a long term project
                    </p>
                  </div>
                </div>

                {/* Follow Button */}
                <Button size="sm" intent="outline">
                  关注
                </Button>
              </div>

              {/* Artwork Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="relative aspect-square overflow-hidden border border-gray-800"
                  >
                    <img
                      src="https://placehold.co/300x300"
                      alt={`Artwork ${item}`}
                      width={300}
                      height={300}
                      className="object-cover w-full h-full"
                    />
                  </div>
                ))}
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
  );
}

function RouteComponent() {
  return (
    <>
      <div className="px-2 sm:px-4 lg:px-6">
        <FeaturedSlides />
      </div>

      <div className="px-2 mt-5 sm:px-4 lg:px-6">
        <Tabs defaultValue="default" activeStyle="border" flavor="graya">
          <TabsList className="mx-auto gap-2 text-lg">
            <TabsTrigger
              className="data-[state=active]:text-gray-fg-primary [&_span]:border-gray-fg-primary"
              value="default"
            >
              推荐
            </TabsTrigger>
            <TabsTrigger
              className="data-[state=active]:text-gray-fg-primary [&_span]:border-gray-fg-primary"
              value="trending"
            >
              热点
            </TabsTrigger>
            <TabsTrigger
              className="data-[state=active]:text-gray-fg-primary [&_span]:border-gray-fg-primary"
              value="latest"
            >
              最新
            </TabsTrigger>
            <TabsTrigger
              className="data-[state=active]:text-gray-fg-primary [&_span]:border-gray-fg-primary"
              value="subscribed"
            >
              关注
            </TabsTrigger>
          </TabsList>

          <AnimatePresence>
            <TabsContent
              key="default"
              value="default"
              className="mt-5 space-y-6"
            >
              <ArtworkGallery type="default" />
              <PromotedArtists />
              <ArtworkGallery type="default" />
            </TabsContent>
            <TabsContent key="trending" value="trending" className="mt-5">
              <ArtworkGallery type="trending" />
            </TabsContent>
            <TabsContent key="latest" value="latest" className="mt-5">
              <ArtworkGallery type="latest" />
            </TabsContent>
            <TabsContent key="subscribed" value="subscribed" className="mt-5">
              <ArtworkGallery type="subscribed" />
            </TabsContent>
          </AnimatePresence>
        </Tabs>
      </div>
    </>
  );
}
